import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import theme from "./styles/theme";
import { ThemeProvider } from "@mui/material/styles";
// MSAL imports
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
} from "@azure/msal-browser";
import { msalConfig, loginRequest, loginRequestWithPrompt } from "./authConfig";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Provider } from "react-redux";
import { persistor, store } from "./store/store";
import { setAccessToken } from "./store/modules/auth/authSlice";
import { PersistGate } from "redux-persist/integration/react";

export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.initialize().then(() => {
  msalInstance
    .handleRedirectPromise()
    .then((response) => {
      if (!response) {
        const accounts = msalInstance.getAllAccounts();
        const isLoggedOutPage = window.location.pathname === "/logged-out";

        if (accounts.length === 0) {
          if (!isLoggedOutPage) {
            // No accounts found, redirect to login
            msalInstance.loginRedirect(loginRequest).catch((e) => {
              console.error("Login redirect failed", e);
            });
          }
        } else {
          const accessToken = store.getState().auth.accessToken;

          if (!accessToken) {
            msalInstance
              .acquireTokenSilent({
                scopes: loginRequest.scopes,
                account: accounts[0],
              })
              .then((response) => {
                if (response) {
                  const { accessToken, account, expiresOn } = response;
                  msalInstance.setActiveAccount(account);

                  store.dispatch(
                    setAccessToken({
                      token: accessToken,
                      expiresIn: expiresOn
                        ? Math.floor((expiresOn.getTime() - Date.now()) / 1000)
                        : 3600,
                    })
                  );
                }
              })
              .catch((error) => {
                console.error("Silent token acquisition failed:", error);
                const isLoggedOutPage =
                  window.location.pathname === "/logged-out";
                if (!isLoggedOutPage) {
                  msalInstance.loginRedirect(loginRequest).catch((e) => {
                    console.error("Login redirect failed", e);
                  });
                }
              });
          }
        }
      } else if (response) {
        const { accessToken, account, expiresOn } = response;
        msalInstance.setActiveAccount(account);
        store.dispatch(
          setAccessToken({
            token: accessToken,
            expiresIn: expiresOn
              ? Math.floor((expiresOn.getTime() - Date.now()) / 1000)
              : 3600,
          })
        );
      }
    })
    .catch(console.error);

  msalInstance.addEventCallback(async (event: EventMessage) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS
    ) {
      const payload = event.payload as AuthenticationResult;

      if (payload) {
        msalInstance.setActiveAccount(payload.account);
        store.dispatch(
          setAccessToken({
            token: payload.accessToken,
            expiresIn: payload.expiresOn
              ? Math.floor((payload.expiresOn.getTime() - Date.now()) / 1000)
              : 3600,
            // role: payload.account.idTokenClaims. ?? undefined,
          })
        );
      }
    } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
      store.dispatch(setAccessToken({ token: "", expiresIn: 0 }));
      sessionStorage.clear();
      localStorage.clear();
      await persistor.purge();

      window.location.href = "/logged-out";
    }
  });

  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );

  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <App pca={msalInstance} />
            </LocalizationProvider>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </React.StrictMode>
  );
});
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
